import React, { useState, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getInventoryByMessageId } from '../../services/inventory';

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background: #292727;
  border-radius: 1rem;
  margin-top: 1rem;
    @media (max-width: 575px) {
      gap: 1px;
      padding: 0;
     background: transparent;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: #ffffff;
  font-size: 1.1rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: #F43F60;
  font-size: 1.1rem;
  text-align: center;
`;

const NoResultsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.1rem;
  text-align: center;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1rem;
  padding: 0.5rem;
`;

const CarCard = styled(motion.div)`
  background: #444343;
  border-radius: 1rem;
  overflow: hidden;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(78, 205, 196, 0.5);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  }
`;

const CarImage = styled.div`
  height: 200px;
  position: relative;
  overflow: hidden;
  justify-content: center;
  align-items: center;
  display: flex;
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
  }
`;

const ImageSlider = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  gap: 0.5rem;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.3);
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }
`;

const Thumbnail = styled.img`
  width: 60px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  scroll-snap-align: start;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const CarInfo = styled.div`
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CarTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 600;
`;

const CarDetails = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

const CarPrice = styled.h3`
 color: #EDEDED;
    font-size: 30px;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.4px;
    text-transform: uppercase;
`;

const NotPricedText = styled.span`
  color: #F43F60;
  font-size: 18px;
  font-weight: 500;
  font-style: italic;
  opacity: 0.8;
  text-transform: none;
  letter-spacing: 0.2px;
`;

const CarTag = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: rgba(78, 205, 196, 0.2);
  color: #4ECDC4;
  border-radius: 1rem;
  font-size: 0.8rem;
  font-weight: 500;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  padding: 0.5rem 0.6rem;
  background: ${props => props.active ? '' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;

  svg {
    width: 16px;
    height: 16px;
    stroke-width: 2.5;
  }

  &:hover {
    background: ${props => props.active ? 'linear-gradient(45deg, #FF6B6B, #4ECDC4)' : 'rgba(255, 255, 255, 0.2)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Ellipsis = styled.span`
  color: rgba(255, 255, 255, 0.5);
  padding: 0 0.5rem;
`;





const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
    align-items: flex-start;
  }
`;

const ModalContent = styled(motion.div)`
  background: #1a1a1a;
  border-radius: 1rem;
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    max-height: 80vh;
    border-radius: 0.5rem;
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: #1a1a1a;
  z-index: 1;

  h2 {
    font-size: 1.5rem;
    margin: 0;
    flex: 1;
    padding-right: 1rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;

    h2 {
      font-size: 1.25rem;
      width: 100%;
      margin-bottom: 0.5rem;
    }
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
    padding: 0.25rem;
  }
`;

const SelectButton = styled.button`
  background: linear-gradient(45deg, #F43F60,rgb(247, 90, 118));
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
`;

const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 0.5rem;
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    height: 70px;
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
  border-radius: 0.5rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    height: 200px;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const DetailItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-radius: 0.5rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const DetailLabel = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const DetailValue = styled.div`
  color: white;
  font-weight: 500;
`;

const CarMileage = styled.div`
color: #EDEDED;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.2;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    -webkit-box-pack: justify;
    -ms-flex-pack: justify;
    justify-content: space-between;
`

const DiscussButton = styled.button`
  background: linear-gradient(45deg, #F43F60,rgb(235, 92, 118));
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-size: 15px;
`;

const DiscussButtonText = styled.span`
  font-size: 15px;
  font-weight: 500;
`;

const CompareCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 16px;
  color: #EDEDED;
  user-select: none;
  
  input[type="checkbox"] {
    display: none;
  }
  
  .checkmark {
    width: 18px;
    height: 18px;
    border: 2px solid #727272;
    border-radius: 4px;
    position: relative;
    transition: all 0.2s ease;
    background: transparent;
  }
  
  input[type="checkbox"]:checked + .checkmark {
    background: #F43F60;
    border-color: #F43F60;
  }
  
  input[type="checkbox"]:checked + .checkmark::after {
    display: block;
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
}
  }
  
  &:hover .checkmark {
    border-color: #F43F60;
  }
`;

const CompareAttachment = styled(motion.div)`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid rgba(244, 63, 96, 0.3);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const CompareInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const CompareIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(45deg, #F43F60, rgb(247, 90, 118));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
`;

const CompareDetails = styled.div`
  color: #ffffff;
  font-size: 14px;
  line-height: 1.4;
`;

const CompareTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
  color: #F43F60;
`;

const CompareSubtitle = styled.div`
  opacity: 0.8;
  font-size: 12px;
`;

const CompareCount = styled.div`
  color: #F43F60;
  font-weight: 700;
  font-size: 16px;
`;

const RemoveButton = styled.button`
  background: rgba(244, 63, 96, 0.2);
  border: 1px solid rgba(244, 63, 96, 0.3);
  color: #F43F60;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(244, 63, 96, 0.3);
    border-color: rgba(244, 63, 96, 0.5);
  }
`;

const CompareButton = styled.button`
  background: linear-gradient(45deg, #F43F60, rgb(247, 90, 118));
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 8px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(244, 63, 96, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ActionButtonsContainer = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FreshSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin: 0rem auto 0;
  max-width: 500px;
  padding: 10px;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  
  
  
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  
  @media (max-width: 768px) {
    margin: 1.5rem auto 0;
    gap: 14px;
    padding: 16px;
  }
  
  @media (max-width: 480px) {
    margin: 1rem auto 0;
    gap: 12px;
    padding: 14px;
  }
`;

const FreshSearchDescription = styled.div`
  text-align: center;
  color: #ffffff;
  font-size: 16px;
  line-height: 1.5;
  font-weight: 500;
  
  .highlight {
    background: linear-gradient(45deg, #F43F60, #FF6B6B);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
    text-shadow: 0 0 20px rgba(244, 63, 96, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const FreshSearchButton = styled.button`
  background: linear-gradient(45deg, #F43F60, #FF6B6B);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 180px;
  box-shadow: 0 4px 15px rgba(244, 63, 96, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(244, 63, 96, 0.4);
    background: linear-gradient(45deg, #E03A56, #F43F60);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 13px;
    min-width: 160px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 12px;
    min-width: 140px;
  }
`;

const FreshSearchIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    width: 14px;
    height: 14px;
  }
  
  @media (max-width: 480px) {
    width: 12px;
    height: 12px;
  }
`;

const SingleCarInterestContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin: 2rem auto 0;
  max-width: 500px;
  padding: 20px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    margin: 1.5rem auto 0;
    gap: 16px;
    padding: 16px;
  }
  
  @media (max-width: 480px) {
    margin: 1rem auto 0;
    gap: 14px;
    padding: 14px;
  }
`;

const SingleCarInterestMessage = styled.div`
  text-align: center;
  color: #ffffff;
  font-size: 18px;
  line-height: 1.5;
  font-weight: 600;
  
  .car-name {
    background: linear-gradient(45deg, #F43F60, #FF6B6B);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
    text-shadow: 0 0 20px rgba(244, 63, 96, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
  
  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const SingleCarButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 400px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const SingleCarButton = styled.button`
  flex: 1;
  background: ${props => props.variant === 'yes' 
    ? 'linear-gradient(45deg, #4ECDC4, #44A08D)' 
    : 'linear-gradient(45deg, #F43F60, #FF6B6B)'};
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 15px ${props => props.variant === 'yes' 
    ? 'rgba(78, 205, 196, 0.3)' 
    : 'rgba(244, 63, 96, 0.3)'};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px ${props => props.variant === 'yes' 
      ? 'rgba(78, 205, 196, 0.4)' 
      : 'rgba(244, 63, 96, 0.4)'};
    background: ${props => props.variant === 'yes' 
      ? 'linear-gradient(45deg, #3BB5A7, #3A8B7A)' 
      : 'linear-gradient(45deg, #E03A56, #F43F60)'};
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 14px;
    font-size: 12px;
  }
`;

const SingleCarLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  border-radius: 1rem;
  margin-top: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  
  
  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1rem;
  }
`;

const SingleCarHeader = styled.div`
  text-align: center;
  margin-bottom: 0.5rem;
`;

const SingleCarTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  background: linear-gradient(45deg, #F43F60, #FF6B6B, #4ECDC4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 20px rgba(244, 63, 96, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.35rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const SingleCarSubtitle = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const SingleCarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SingleCarImageContainer = styled.div`
  position: relative;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
  background: linear-gradient(135deg, #2a2a2a, #1a1a1a);
  width: 100%;
  margin-bottom: 0.5rem;
`;

const SingleCarMainImage = styled.img`
  width: 100%;
  height: 320px;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
  }
  
  @media (max-width: 768px) {
    height: 280px;
  }
  
  @media (max-width: 480px) {
    height: 220px;
  }
`;

const SingleCarImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 1.5rem 1.25rem 1rem;
  color: white;
`;

const SingleCarImageTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.35rem 0;
  color: white;
`;

const SingleCarImageSubtitle = styled.p`
  font-size: 0.9rem;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
`;

const SingleCarDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.875rem;
  }
`;

const SingleCarPrice = styled.div`
  text-align: center;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(244, 63, 96, 0.1), rgba(78, 205, 196, 0.1));
  border-radius: 0.75rem;
  border: 1px solid rgba(244, 63, 96, 0.2);
  grid-column: 1 / -1;
`;

const SingleCarPriceLabel = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-weight: 600;
`;

const SingleCarPriceValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #F43F60;
  text-shadow: 0 0 20px rgba(244, 63, 96, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SingleCarSpecs = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 0.75rem;
  grid-column: 1 / -1; /* span full width of parent grid to allow more columns */
`;

const SingleCarSpec = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.5rem;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SingleCarSpecLabel = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
`;

const SingleCarSpecValue = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: white;
`;

const SingleCarActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  grid-column: 1 / -1;
`;

const SingleCarActionButton = styled.button`
  background: ${props => props.variant === 'primary' 
    ? 'linear-gradient(45deg, #4ECDC4, #44A08D)' 
    : 'linear-gradient(45deg, #F43F60, #FF6B6B)'};
  color: white;
  border: none;
  border-radius: 0.75rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 6px 20px ${props => props.variant === 'primary' 
    ? 'rgba(78, 205, 196, 0.3)' 
    : 'rgba(244, 63, 96, 0.3)'};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px ${props => props.variant === 'primary' 
      ? 'rgba(78, 205, 196, 0.4)' 
      : 'rgba(244, 63, 96, 0.4)'};
    background: ${props => props.variant === 'primary' 
      ? 'linear-gradient(45deg, #3BB5A7, #3A8B7A)' 
      : 'linear-gradient(45deg, #E03A56, #F43F60)'};
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 0.675rem 1.25rem;
    font-size: 0.875rem;
  }
`;

const SingleCarSecondaryButton = styled.button`
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 0.75rem;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 0.55rem 1.1rem;
    font-size: 0.825rem;
  }
`;

const OfferSection = styled.div`
  background: linear-gradient(135deg, rgba(78, 205, 196, 0.1), rgba(244, 63, 96, 0.1));
  border: 1px solid rgba(78, 205, 196, 0.2);
  border-radius: 0.75rem;
  padding: 1rem;
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    padding: 0.875rem;
  }
`;

const OfferTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const OfferInputContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: stretch;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.625rem;
  }
`;

const OfferInputWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const OfferDollarSign = styled.span`
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.1rem;
  font-weight: 600;
  pointer-events: none;
`;

const OfferInput = styled.input`
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.25rem;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  color: white;
  font-size: 1.05rem;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #4ECDC4;
    background: rgba(255, 255, 255, 0.15);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  @media (max-width: 768px) {
    font-size: 0.975rem;
    padding: 0.675rem 0.675rem 0.675rem 2rem;
  }
`;

const OfferButton = styled.button`
  background: linear-gradient(45deg, #4ECDC4, #44A08D);
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 6px 20px rgba(78, 205, 196, 0.3);
  white-space: nowrap;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(78, 205, 196, 0.4);
    background: linear-gradient(45deg, #3BB5A7, #3A8B7A);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0.675rem 1.25rem;
    font-size: 0.875rem;
  }
`;

const CarSearchResults = ({ onCarSelect, compareList = [], onCompareChange, onCompareClick, messageId, setSelectedCara, onFreshSearch, onCarSelectWithOffer }) => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 12,
    pages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCar, setSelectedCar] = useState(null);
  const [mainImage, setMainImage] = useState({});
  const [offerPrice, setOfferPrice] = useState('');
  const navigate = useNavigate();

  // Function to handle compare checkbox changes
  const handleCompareChange = (carId, checked) => {
    if (checked) {
      onCompareChange([...compareList, carId]);
      
    } else {
      // Remove car from compare list
      onCompareChange(compareList.filter(id => id !== carId));
    }
  };

  // Check if a car is in compare list
  const isCarInCompareList = (carId) => {
    return compareList.includes(carId);
  };

  // Get cars to compare
  const getCarsToCompare = () => {
    return cars.filter(car => compareList.includes(car._id));
  };

  // Fetch cars from API
  const fetchCars = async (messageId, page = 1) => {
    if (!messageId ) {
      setCars([]);
      setPagination(prev => ({ ...prev, total: 0, pages: 0 }));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getInventoryByMessageId({
        messageId: messageId,
        page,
        limit: pagination.limit
      });

      if (response.status === 'success') {
        // Transform the data to replace imagelist with images and fix keys
        const transformedVehicles = response.data.vehicles.map(vehicle => {
          let images = [];
          try {
            // Parse imagelist if it exists and is a string
            if (vehicle.imagelist && typeof vehicle.imagelist === 'string') {
              try {
                images = JSON.parse(vehicle.imagelist);
              } catch {
                images = vehicle.imagelist.split(',').map(url => ({ url: url }));
              }
            }
          } catch (e) {
            console.error('Error parsing imagelist:', e);
           
          }

          return {
            ...vehicle,
            images: images,
            _id: vehicle.id, // Map id to _id for consistency
            key: vehicle.id // Ensure key is set to id
          };
        });

        setCars(transformedVehicles);
        setPagination(response.data.pagination);
      } else {
        setError(response.message || 'Failed to fetch cars');
        setCars([]);
      }
    } catch (err) {
      console.error('Error fetching cars:', err);
      setError('Failed to fetch cars. Please try again.');
      setCars([]);
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch cars when messageId or page changes
  useEffect(() => {
    if (messageId) {
      fetchCars(messageId, currentPage);
    }
  }, [messageId, currentPage]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top when page changes
    // window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Use cars directly from API (server-side filtering)
  const paginatedCars = cars;

  const handleCarClick = (car) => {
    setSelectedCar(car);
    setMainImage({ [car._id]: car.images[0]?.url });
  };

  const handleImageClick = (carId, imageUrl) => {
    setMainImage(prev => ({
      ...prev,
      [carId]: imageUrl
    }));
  };

  const handleSelectCar = () => {
    if (selectedCar && onCarSelect) {
      onCarSelect(selectedCar);
      setSelectedCar(null);
    }
  };

  const handleCarDetailsClick = (carId) => {
    window.open(`/car-details?id=${carId}`, '_blank');
  };

  const handleFreshSearch = () => {
    if (onFreshSearch) {
      onFreshSearch("I want to start a new search");
    }
  };

  const handleSingleCarInterest = (car) => {
    if (onCarSelect) {
      onCarSelect(car);
    }
  };

  const handleSingleCarNewSearch = () => {
    if (onFreshSearch) {
      onFreshSearch("I want to start a new search");
    }
  };

  const handleMakeOffer = () => {
    if (offerPrice && cars.length === 1) {
      const car = cars[0];
      if (onCarSelectWithOffer) {
        const formattedPrice = parseInt(offerPrice.replace(/,/g, '')).toLocaleString();
        setSelectedCara(car);
        onCarSelectWithOffer(car, formattedPrice);
      }
      setOfferPrice('');
    }
  };

  const handleOfferPriceChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value) {
      setOfferPrice(parseInt(value).toLocaleString());
    } else {
      setOfferPrice('');
    }
  };

  const renderPagination = () => {
    if (pagination.pages <= 1) return null;

    const pages = [];
    const showEllipsisStart = currentPage > 2;
    const showEllipsisEnd = currentPage < pagination.pages - 1;

    // First page
    pages.push(
      <PageButton
        key="first"
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
        title="First Page"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5"/>
        </svg>
      </PageButton>
    );

    // Previous page
    pages.push(
      <PageButton
        key="prev"
        onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
        disabled={currentPage === 1}
        title="Previous Page"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6"/>
        </svg>
      </PageButton>
    );

    // Page numbers
    if (showEllipsisStart) {
      pages.push(
        <PageButton key="1" onClick={() => setCurrentPage(1)}>
          1
        </PageButton>
      );
      pages.push(<Ellipsis key="ellipsis-start">...</Ellipsis>);
    }

    // Current page and surrounding pages
    for (let i = Math.max(1, currentPage - 1); i <= Math.min(pagination.pages, currentPage + 1); i++) {
      pages.push(
        <PageButton
          key={i}
          active={currentPage === i}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </PageButton>
      );
    }

    if (showEllipsisEnd) {
      pages.push(<Ellipsis key="ellipsis-end">...</Ellipsis>);
      pages.push(
        <PageButton key={pagination.pages} onClick={() => handlePageChange(pagination.pages)}>
          {pagination.pages}
        </PageButton>
      );
    }

    // Next page
    pages.push(
      <PageButton
        key="next"
        onClick={() => handlePageChange(Math.min(currentPage + 1, pagination.pages))}
        disabled={currentPage === pagination.pages}
        title="Next Page"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </PageButton>
    );

    // Last page
    pages.push(
      <PageButton
        key="last"
        onClick={() => handlePageChange(pagination.pages)}
        disabled={currentPage === pagination.pages}
        title="Last Page"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <path d="M13 17l5-5-5-5M6 17l5-5-5-5"/>
        </svg>
      </PageButton>
    );

    return pages;
  };

  return (
    <ResultsContainer>
      {compareList.length > 0 && (
        <CompareAttachment
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <CompareInfo>
            <CompareIcon>{compareList.length}</CompareIcon>
            <CompareDetails>
              <CompareTitle>{compareList.length} car{compareList.length > 1 ? 's' : ''} selected for comparison</CompareTitle>
              <CompareSubtitle>Click "Compare Now" to see details and options.</CompareSubtitle>
            </CompareDetails>
          </CompareInfo>
          <RemoveButton onClick={onCompareClick}>
            <X size={16} />
          </RemoveButton>
        </CompareAttachment>
      )}

      {loading && (
        <LoadingContainer>
          Loading cars...
        </LoadingContainer>
      )}

      {error && (
        <ErrorContainer>
          {error}
        </ErrorContainer>
      )}

      {!loading && !error && cars.length === 0 && messageId && (
        <NoResultsContainer>
          <div style={{ textAlign: 'center', maxWidth: '400px', margin: '0 auto' }}>
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '16px',
              opacity: 0.5
            }}>
              🚗
            </div>
            <div style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              marginBottom: '12px',
              color: '#ffffff'
            }}>
              No vehicles found
            </div>
            <div style={{ 
              fontSize: '14px', 
              lineHeight: '1.5',
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              We couldn't find any vehicles matching your search criteria. 
              Try adjusting your search terms.
            </div>
          </div>
        </NoResultsContainer>
      )}

      {!loading && !error && cars.length > 1 && (
        <GridContainer>
          <AnimatePresence>
            {paginatedCars.map((car) => (
            <CarCard
              key={car._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <CarImage>
                <img 
                  src={mainImage[car._id] || car.images[0]?.url} 
                  alt={`${car.make} ${car.model}`} 
                />
                {/* <ImageSlider>
                  {car.images.slice(0, 5).map((image, index) => (
                    <Thumbnail
                      key={image._id}
                      src={image.url}
                      alt={`${car.make} ${car.model} - Image ${index + 1}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageClick(car._id, image.url);
                      }}
                    />
                  ))}
                </ImageSlider> */}
              </CarImage>
              <CarInfo>
                <CarTitle>{car.year} {car.make} {car.model}</CarTitle>

                <CarDetails>
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', 'marginBottom': '12px'}}>
                   
                   <CarMileage>{parseInt(car.mileage)?.toLocaleString()} mi</CarMileage>
                   <CompareCheckbox>
                     Compare
                     <input 
                       type="checkbox" 
                       checked={isCarInCompareList(car._id)}
                       onChange={(e) => handleCompareChange(car._id, e.target.checked)}
                     />
                                        <span className="checkmark"></span>
                   </CompareCheckbox>     
                                    
                                                 </div>
                                               

                </CarDetails>
                <ActionButtonsContainer>
                <hr style={{
                                                  borderBottom: '1px solid #727272',
                                                   margin: '0.5em 0',
                                                  border: 0,
                                                  borderTop: '1px solid',
                                                  }} />
                                                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', 'marginBottom': '12px'}}>

                                                  <CarPrice>
                                                    {parseInt(car.price) === 0 ? (
                                                      <NotPricedText>Not Priced Yet</NotPricedText>
                                                    ) : (
                                                      `$${parseInt(car.price)?.toLocaleString()}`
                                                    )}
                                                  </CarPrice>
                                                  <div style={{
                                                        display: 'block',
                                                        color: '#fff',
                                                        fontSize: '24px',
                                                        height: '40px',
                                                        width: '40px',
                                                        borderRadius: '50%',
                                                        lineHeight: '40px',
                                                        background: '#292929',
                                                        textAlign: 'center',
                                                        cursor: 'pointer',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        display: 'flex',
                                                  }}
                  onClick={() => handleCarDetailsClick(car._id)}
                                                  >
                                                    <ArrowRight />
                                                  </div>

                                                  </div>
                  <DiscussButton
                    onClick={() => onCarSelect(car)}
                  >
                    <DiscussButtonText>Interested In This</DiscussButtonText>
                  </DiscussButton>
                  <DiscussButton
                    onClick={() => navigate(`/dealer-inventory/${car.dealer_id}`)}
                    style={{ 
                      background: 'linear-gradient(45deg, #4ECDC4, #44A08D)',
                      fontSize: '14px',
                      padding: '8px 16px'
                    }}
                  >
                    <DiscussButtonText>Show more from dealer</DiscussButtonText>
                  </DiscussButton>
                </ActionButtonsContainer>
              </CarInfo>
            </CarCard>
          ))}
        </AnimatePresence>
      </GridContainer>
      )}

      {pagination.pages > 1 && cars.length > 1 && (
        <PaginationContainer>
          {renderPagination()}
        </PaginationContainer>
      )}

      {!loading && !error && cars.length === 1 && (
        <SingleCarLayout>
          <SingleCarHeader>
            <SingleCarTitle>Perfect Match Found!</SingleCarTitle>
            <SingleCarSubtitle>We found exactly what you're looking for</SingleCarSubtitle>
          </SingleCarHeader>
          
          <SingleCarContent>
            <SingleCarImageContainer>
              <SingleCarMainImage 
                src={mainImage[cars[0]._id] || cars[0].images[0]?.url} 
                alt={`${cars[0].year} ${cars[0].make} ${cars[0].model}`}
              />
              <SingleCarImageOverlay>
                <SingleCarImageTitle>{cars[0].year} {cars[0].make} {cars[0].model}</SingleCarImageTitle>
                <SingleCarImageSubtitle>
                  {parseInt(cars[0].mileage)?.toLocaleString()} miles • {cars[0].new_used}
                  {cars[0].certified === 'certified' && ' • Certified'}
                </SingleCarImageSubtitle>
              </SingleCarImageOverlay>
            </SingleCarImageContainer>
            
            <SingleCarDetails>
              <SingleCarPrice>
                <SingleCarPriceLabel>Price</SingleCarPriceLabel>
                <SingleCarPriceValue>
                  {parseInt(cars[0].price) === 0 ? (
                    <span style={{ color: '#F43F60', fontSize: '1.5rem', fontStyle: 'italic' }}>
                      Not Priced Yet
                    </span>
                  ) : (
                    `$${parseInt(cars[0].price)?.toLocaleString()}`
                  )}
                </SingleCarPriceValue>
              </SingleCarPrice>
              
              <SingleCarSpecs>
                <SingleCarSpec>
                  <SingleCarSpecLabel>Mileage</SingleCarSpecLabel>
                  <SingleCarSpecValue>{parseInt(cars[0].mileage)?.toLocaleString()} mi</SingleCarSpecValue>
                </SingleCarSpec>
                <SingleCarSpec>
                  <SingleCarSpecLabel>Engine</SingleCarSpecLabel>
                  <SingleCarSpecValue>{cars[0].engine || 'N/A'}</SingleCarSpecValue>
                </SingleCarSpec>
                <SingleCarSpec>
                  <SingleCarSpecLabel>Transmission</SingleCarSpecLabel>
                  <SingleCarSpecValue>{cars[0].transmission || 'N/A'}</SingleCarSpecValue>
                </SingleCarSpec>
                <SingleCarSpec>
                  <SingleCarSpecLabel>Drive Train</SingleCarSpecLabel>
                  <SingleCarSpecValue>{cars[0].drivetrain || 'N/A'}</SingleCarSpecValue>
                </SingleCarSpec>
                <SingleCarSpec>
                  <SingleCarSpecLabel>Exterior</SingleCarSpecLabel>
                  <SingleCarSpecValue>{cars[0].ext_color || 'N/A'}</SingleCarSpecValue>
                </SingleCarSpec>
                <SingleCarSpec>
                  <SingleCarSpecLabel>Interior</SingleCarSpecLabel>
                  <SingleCarSpecValue>{cars[0].int_color || 'N/A'}</SingleCarSpecValue>
                </SingleCarSpec>
               
               
              </SingleCarSpecs>
              
              <OfferSection>
                <OfferTitle>Ready to make an offer for this car?</OfferTitle>
                <OfferInputContainer>
                  <OfferInputWrapper>
                    <OfferDollarSign>$</OfferDollarSign>
                    <OfferInput
                      type="text"
                      placeholder="Enter your offer"
                      value={offerPrice}
                      onChange={handleOfferPriceChange}
                    />
                  </OfferInputWrapper>
                  <OfferButton 
                    onClick={handleMakeOffer}
                    disabled={!offerPrice}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    Send Offer
                  </OfferButton>
                </OfferInputContainer>
              </OfferSection>
              
              <SingleCarActions>
                <SingleCarActionButton 
                  variant="primary" 
                  onClick={() => handleSingleCarInterest(cars[0])}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  Yes, I'm interested in this car
                </SingleCarActionButton>
                
                <SingleCarSecondaryButton onClick={() => handleCarDetailsClick(cars[0]._id)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  View Full Details
                </SingleCarSecondaryButton>
                
                <SingleCarSecondaryButton onClick={() => navigate(`/dealer-inventory/${cars[0].dealer_id}`)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9,22 9,12 15,12 15,22"/>
                  </svg>
                  Show more from this dealer
                </SingleCarSecondaryButton>
                
                <SingleCarActionButton 
                  variant="secondary" 
                  onClick={handleSingleCarNewSearch}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                    <path d="M21 3v5h-5"/>
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                    <path d="M3 21v-5h5"/>
                  </svg>
                  No, start a new search
                </SingleCarActionButton>
              </SingleCarActions>
            </SingleCarDetails>
          </SingleCarContent>
        </SingleCarLayout>
      )}

      {!loading && !error && cars.length > 1 && (
        <FreshSearchContainer>
          <FreshSearchDescription>
            Not finding what you're looking for? <span className="highlight">Start a new search</span> with different criteria.
          </FreshSearchDescription>
          <FreshSearchButton onClick={handleFreshSearch}>
            <FreshSearchIcon>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                <path d="M21 3v5h-5"/>
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                <path d="M3 21v-5h5"/>
              </svg>
            </FreshSearchIcon>
            New Search
          </FreshSearchButton>
        </FreshSearchContainer>
      )}

      <AnimatePresence>
        {selectedCar && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedCar(null)}
          >
            <ModalContent
              onClick={e => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <ModalHeader>
                <h2>{selectedCar.year} {selectedCar.make} {selectedCar.model}</h2>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <SelectButton onClick={handleSelectCar}>
                    Select Car
                  </SelectButton>
                  <CloseButton onClick={() => setSelectedCar(null)}>×</CloseButton>
                </div>
              </ModalHeader>
              <ModalBody>
                <div>
                  <MainImage 
                    src={mainImage[selectedCar._id] || selectedCar.images[0]?.url} 
                    alt={`${selectedCar.make} ${selectedCar.model}`} 
                  />
                  <ImageGallery>
                    {selectedCar.images.map((image, index) => (
                      <GalleryImage
                        key={index}
                        src={image.url}
                        alt={`${selectedCar.make} ${selectedCar.model}`}
                        onClick={() => handleImageClick(selectedCar._id, image.url)}
                      />
                    ))}
                  </ImageGallery>
                </div>
                <div>
                  <DetailsGrid>
                    <DetailItem>
                      <DetailLabel>Price</DetailLabel>
                      <DetailValue>
                        {parseInt(selectedCar.price) === 0 ? (
                          <NotPricedText>Not Priced Yet</NotPricedText>
                        ) : (
                          `$${parseInt(selectedCar.price)?.toLocaleString()}`
                        )}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Mileage</DetailLabel>
                      <DetailValue>{parseInt(selectedCar.mileage)?.toLocaleString()} miles</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Engine</DetailLabel>
                      <DetailValue>{selectedCar.engine}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Transmission</DetailLabel>
                      <DetailValue>{selectedCar.transmission}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Drive Train</DetailLabel>
                      <DetailValue>{selectedCar.drivetrain}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Exterior Color</DetailLabel>
                      <DetailValue>{selectedCar.ext_color}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Interior Color</DetailLabel>
                      <DetailValue>{selectedCar.int_color}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Stock ID</DetailLabel>
                      <DetailValue>{selectedCar.stock_no}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>VIN</DetailLabel>
                      <DetailValue>{selectedCar.vin}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Status</DetailLabel>
                      <DetailValue>
                        {selectedCar.new_used}
                        {selectedCar.certified === 'certified' && ' • Certified'}
                      </DetailValue>
                    </DetailItem>
                  </DetailsGrid>
                  {selectedCar.description && (
                    <DetailItem style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                      <DetailLabel>Description</DetailLabel>
                      <DetailValue>{selectedCar.description}</DetailValue>
                    </DetailItem>
                  )}
                </div>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </ResultsContainer>
  );
};

export default CarSearchResults; 